import { Component, Input, OnDestroy, OnInit } from '@angular/core';
import { DialogService, DynamicDialogRef } from 'primeng/dynamicdialog';
import { Subscription } from 'rxjs';
import { NotificationsService } from 'src/app/common/services/notifications/notifications.service';
import { ConfirmDialogComponent } from '../../../shared/components/confirm-dialog/confirm-dialog.component';
import { NotificationInformationDialogComponent } from '../notification-information.dialog/notification-information.dialog';
import * as moment from 'moment';
import { INotification } from 'src/app/common/model/notification.model';
import {
  PushNotifications,
  Token,
  PushNotificationSchema,
  ActionPerformed
} from '@capacitor/push-notifications';
import { Capacitor } from '@capacitor/core';

@Component({
  selector: 'app-notification-list',
  templateUrl: './notification-list.component.html',
  styleUrls: ['./notification-list.component.scss']
})
export class NotificationListComponent implements OnInit, OnDestroy {
  subscriptions = new Subscription();
  allNotifications: INotification[] = [];
  unreadNotifications: INotification[] = [];
  notificationCount = 0;
  ref!: DynamicDialogRef | undefined;
  isLoading = false;
  all = false;
  unread = true;
  @Input() panelView = false;

  constructor(
    private notificationService: NotificationsService,
    private dialogService: DialogService
  ) {}

  ngOnInit(): void {
    this.getUnreadNotifications();
    this.getAllNotifications();
    this.initializePushNotifications();
  }

  ngOnDestroy(): void {
    this.subscriptions.unsubscribe();
  }

  switch(value?: string) {
    this.all = !this.all;
    this.unread = !this.unread;
  }

  initializePushNotifications(): void {
    if (Capacitor.isNativePlatform()) {
      this.setupPushNotifications();
    } else {
    }
  }

  setupPushNotifications(): void {
    PushNotifications.requestPermissions().then((result) => {
      if (result.receive === 'granted') {
        // Register with Apple / Google to receive push via APNS/FCM
        PushNotifications.register();
      } else {
        // Show some error
        console.error('Push notification permission not granted');
      }
    });

    // On success, we should be able to receive notifications
    PushNotifications.addListener('registration', (token: Token) => {
      alert('Push registration success');
    });

    // Some issue with our setup and push will not work
    PushNotifications.addListener('registrationError', (error: any) => {
      alert('Error on registration: ' + JSON.stringify(error));
    });

    // Show us the notification payload if the app is open on our device
    PushNotifications.addListener(
      'pushNotificationReceived',
      (notification: PushNotificationSchema) => {
        alert('Push received: ' + JSON.stringify(notification));
      }
    );

    // Method called when tapping on a notification
    PushNotifications.addListener(
      'pushNotificationActionPerformed',
      (notification: ActionPerformed) => {
        alert('Push action performed: ' + JSON.stringify(notification));
      }
    );
  }

  getUnreadNotifications(): void {
    this.isLoading = true;
    this.subscriptions.add(
      this.notificationService.getUnreadNotifications().subscribe({
        next: (res) => {
          this.isLoading = false;
          this.unreadNotifications = res.result;
          this.notificationService.notificationsCount$.subscribe((count) => {
            this.notificationCount = count;
          });
          this.notificationService.updateNotificationsCount(
            this.unreadNotifications.length
          );
          this.unreadNotifications?.map((notification) => {
            notification = this.appendDropdownItemsToItem(notification);
            notification.time = moment(notification.createdAt).fromNow();
            if (notification.moduleType.toUpperCase() === 'MEETING') {
              notification.relatedItemID?.start &&
                (notification.relatedItemID.start = moment(
                  notification.relatedItemID?.start
                ).format('DD/MM/YY hh:mm A'));
              notification.relatedItemID?.end &&
                (notification.relatedItemID.end = moment(
                  notification.relatedItemID?.end
                ).format('DD/MM/YY hh:mm A'));
            }
          });
        },
        error: () => (this.isLoading = false)
      })
    );
  }

  getAllNotifications(): void {
    this.isLoading = true;
    this.subscriptions.add(
      this.notificationService.getAllNotifications().subscribe({
        next: (res) => {
          this.isLoading = false;
          this.allNotifications = res.result;
          this.notificationService.notificationsCount$.subscribe((count) => {
            this.notificationCount = count;
          });
          this.allNotifications?.map((notification) => {
            notification = this.appendDropdownItemsToItem(notification);
            notification.time = moment(notification.createdAt).fromNow();
            if (notification.moduleType.toUpperCase() === 'MEETING') {
              notification.relatedItemID?.start &&
                (notification.relatedItemID.start = moment(
                  notification.relatedItemID?.start
                ).format('DD/MM/YY hh:mm A'));
              notification.relatedItemID?.end &&
                (notification.relatedItemID.end = moment(
                  notification.relatedItemID?.end
                ).format('DD/MM/YY hh:mm A'));
            }
          });
        },
        error: () => {
          this.isLoading = false;
        }
      })
    );
  }

  markAllAsRead() {
    this.ref = this.dialogService.open(ConfirmDialogComponent, {
      header: 'Notifications',
      width: '50%',
      data: {
        message: 'All Notifications will be marked as read.'
      }
    });
    this.ref.onClose.subscribe((confirm) => {
      if (confirm) {
        this.notificationService.markAllNotificationAsRead().subscribe((data) => {
          this.allNotifications.forEach((notification) => (notification.isRead = true));
          this.notificationService.updateNotificationsCount(0);
          window.location.reload();
        });
      }
    });
  }

  markAsRead(notification: INotification) {
    this.notificationService
      .markNotificationAsRead(notification._id!)
      .subscribe((data) => {
        const index = this.unreadNotifications.findIndex(
          (update) => update._id === notification._id
        );
        this.notificationService.getUnreadNotificationCount();
        this.unreadNotifications[index].isRead = true;
      });
  }

  viewDetails(notification: INotification) {
    this.ref = this.dialogService.open(NotificationInformationDialogComponent, {
      header: 'Notification details',
      width: '45%',
      data: notification
    });
  }

  appendDropdownItemsToItem(notification: INotification): INotification {
    const tableActionButtons = [
      {
        label: 'Mark as read',
        icon: 'pi pi-eye',
        command: () => this.markAsRead(notification),
        disabled: notification.isRead
      }
    ];
    notification.menuItems = tableActionButtons;
    return notification;
  }
}
